import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BillStatus, BookingShare, CondimentQty, CondimentSaleDTO, CreateBillRequest, CustomerDTO, PaymentMethod } from '@six-pockets/shared';
import { billsApi, condimentSalesApi, customersApi } from '@/api';
import { useBills } from '@/context/BillsContext';
import { useTableActions, type SelectedBooking } from '@/context/TablesContext';
import { todayKey } from '@/utils/date';
import { toAmount } from '@/utils/format';
import { clearBillDraft, readBillDraft, saveBillDraft, type BillOpenedWith, type BillSheet, type BillStep } from './billDraft';
import { computeBill, type BillTotals } from './calc';
import { addBill, equalParts, reconcile, removeBill, setBillTotal, setPart, spread, type Parts } from './split';

/** How long the "Bill generated" tick shows before the modal closes. */
const SUCCESS_MS = 1100;
/** The most bills one modal can split into (singles: four players). */
export const MAX_BILLS = 4;

/**
 * The customer everything the bill opens with was given (a booking's name or a tab's name picked from the existing
 * customers), when it is one and the same for all of it. Null if anything has only a typed name, or they differ.
 */
function sharedCustomerId(bookings: SelectedBooking[], sales: CondimentSaleDTO[]): string | null {
  const ids = [...bookings, ...sales].map((x) => x.customerId);
  const first = ids[0];
  return first && ids.every((id) => id === first) ? first : null;
}

function newSheet(saleIds: string[] = [], name = ''): BillSheet {
  return { name, saleIds, customer: null, discount: '', status: 'settled', method: 'upi', upi: '', cash: '', paid: '' };
}

/** Someone paying: a booking's name (first or second), the same person when it is the same customer or name. */
interface Payer {
  key: string;
  name: string;
  customerId: string | null;
}

function payersOf(b: SelectedBooking): Payer[] {
  const names: [string, string | null][] = [
    [b.customerName, b.customerId],
    [b.customerName2, b.customerId2],
  ];
  return names
    .filter(([name]) => name.trim())
    .map(([name, customerId]) => ({ key: customerId ?? name.trim().toLowerCase(), name: name.trim(), customerId }));
}

/**
 * Doubles: when a ticked booking has a second name, the bill opens split, one bill per name (2 to MAX_BILLS of them,
 * in the order met). Each booking is shared equally between its own names; one without a name between all bills.
 * Staff then change what each pays. Null when there is nothing to split by.
 */
function splitByNames(bookings: SelectedBooking[]): { sheets: BillSheet[]; parts: Parts; customerIds: (string | null)[] } | null {
  if (!bookings.some((b) => b.customerName2.trim())) return null;
  const payers: Payer[] = [];
  for (const b of bookings) for (const p of payersOf(b)) if (!payers.some((x) => x.key === p.key)) payers.push(p);
  if (payers.length < 2 || payers.length > MAX_BILLS) return null;

  const parts: Parts = {};
  for (const b of bookings) {
    const mine = new Set(payersOf(b).map((p) => p.key));
    const weights = payers.map((p) => (!mine.size || mine.has(p.key) ? 1 : 0));
    parts[b.id] = spread(b.amount ?? 0, weights);
  }
  return { sheets: payers.map((p) => newSheet([], p.name)), parts, customerIds: payers.map((p) => p.customerId) };
}

/** A bill's bookings for the request: whole ones by id, the split ones as this bill's part. Nothing for ₹0. */
function bookingsOf(bookings: SelectedBooking[], parts: Parts | null, sheet: number): { bookingIds: string[]; shares: BookingShare[] } {
  if (!parts) return { bookingIds: sheet === 0 ? bookings.map((b) => b.id) : [], shares: [] };
  const bookingIds: string[] = [];
  const shares: BookingShare[] = [];
  for (const b of bookings) {
    const part = parts[b.id]?.[sheet] ?? 0;
    if (part <= 0) continue;
    if (part === (b.amount ?? 0)) bookingIds.push(b.id);
    else shares.push({ bookingId: b.id, amount: part });
  }
  return { bookingIds, shares };
}

/** A booking as one bill sees it: its part of the amount and of the pause. Left out when the bill has none of it. */
export interface SheetBooking {
  id: string;
  amount: number;
  pausedMs: number;
  pauseAmount: number;
}

function sheetBookings(bookings: SelectedBooking[], parts: Parts | null, sheet: number): SheetBooking[] {
  if (!parts) {
    return sheet === 0 ? bookings.map((b) => ({ id: b.id, amount: b.amount ?? 0, pausedMs: b.pausedMs, pauseAmount: b.pauseAmount ?? 0 })) : [];
  }
  return bookings.flatMap((b) => {
    const p = parts[b.id] ?? [];
    const amount = p[sheet] ?? 0;
    if (amount <= 0) return [];
    return [{ id: b.id, amount, pausedMs: b.pausedMs, pauseAmount: spread(b.pauseAmount ?? 0, p)[sheet] ?? 0 }];
  });
}

/**
 * All state, derived totals and the submit action for the bill modal.
 * `bookings`: the ticked ended bookings (any tables). `initialSales`: condiment tabs to tick from the start
 * ("Generate Bill" on the Condiments page). Other tabs opened today can be added in the modal.
 *
 * Split (doubles: one booking, each team pays its part) turns the one bill into 2 to 4 bills ("sheets"), each with
 * its name (the booking's names, when it opened split by them; else "Bill 2"),
 * its own tabs, customer, discount, status and payment, and each booking's amount shared between them (`parts`).
 * They are generated together, all or none, as ordinary bills.
 */
export function useBillForm(bookings: SelectedBooking[], initialSales: CondimentSaleDTO[], onDone: () => void) {
  const { createBill, createBills } = useBills();
  const { refreshOpenSales } = useTableActions();

  // A bill closed before it was generated opens again as it was left (see billDraft.ts): the step, the bills, their
  // tabs, customers, discounts, statuses and payments, and the split.
  const [openedWith] = useState<BillOpenedWith>(() => ({ bookingIds: bookings.map((b) => b.id), saleIds: initialSales.map((s) => s.id) }));
  const [draft] = useState(() => readBillDraft(openedWith));

  // A new bill whose bookings carry two names opens split by them (see splitByNames).
  const [byNames] = useState(() => (draft ? null : splitByNames(bookings)));

  const [step, setStep] = useState<BillStep>(draft?.step ?? 'items');
  const [sheets, setSheets] = useState<BillSheet[]>(() => {
    const list = draft?.sheets.length ? draft.sheets : byNames ? byNames.sheets : [newSheet()];
    // The tab(s) the bill was started from are always on it when it opens (on the first bill unless already on one).
    const missing = initialSales.map((s) => s.id).filter((id) => !list.some((sh) => sh.saleIds.includes(id)));
    return list.map((sh, i) => (i === 0 ? { ...sh, saleIds: [...missing, ...sh.saleIds] } : sh));
  });
  const [parts, setParts] = useState<Parts | null>(() =>
    byNames
      ? byNames.parts
      : draft?.parts && draft.sheets.length > 1
        ? reconcile(draft.parts, bookings, draft.sheets.length)
        : null,
  );
  const [active, setActive] = useState(() => Math.min(draft?.active ?? 0, (draft?.sheets.length ?? 1) - 1));
  /** What happy hour gives each bill's bookings; 0 when none of their time was in it (or while loading). */
  const [happyHours, setHappyHours] = useState<number[]>([]);
  const [billNumber, setBillNumber] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** The bills were generated; the modal closes after a short success state. */
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Open condiment tabs; null while loading. */
  const [sales, setSales] = useState<CondimentSaleDTO[] | null>(null);
  const [salesError, setSalesError] = useState<string | null>(null);
  /** The tab being edited inline in the bill (an id, 'new' for the New tab form, or null). One at a time. */
  const [editingTab, setEditingTab] = useState<string | 'new' | null>(null);

  const isSplit = sheets.length > 1;
  const sheet = sheets[active] ?? sheets[0]!;

  useEffect(() => {
    // Preview only; the server assigns the final number when the bill is saved.
    billsApi.nextNumber().then((r) => setBillNumber(r.billNumber), () => setBillNumber(null));
    // One customer on everything the bill opens with: they are selected on the first bill already (unless staff
    // picked someone meanwhile). Split by names: each bill gets its name's customer, when it is one. If they can't
    // be loaded, staff pick as usual.
    const customerIds = byNames ? byNames.customerIds : [sharedCustomerId(bookings, initialSales)];
    customerIds.forEach((customerId, index) => {
      if (!customerId) return;
      customersApi.get(customerId).then(
        (c) => setSheets((list) => list.map((sh, i) => (i === index && !sh.customer ? { ...sh, customer: c } : sh))),
        () => undefined,
      );
    });
    // Only tabs opened today (the server caps a page at 100), plus the tab(s) the bill was started from,
    // which may be from an earlier day (picked in the Condiments page's date filter).
    condimentSalesApi.list({ status: 'open', date: todayKey(), pageSize: 100 }).then(
      (page) => setSales([...initialSales.filter((s) => !page.items.some((p) => p.id === s.id)), ...page.items]),
      (err) => setSalesError(err instanceof Error ? err.message : 'Could not load condiment tabs'),
    );
  }, []);

  // Happy hour, worked out by the server from each bill's bookings (its parts of the split ones) and the settings.
  // Asked again (after a short pause) when the split changes. If it can't be loaded, the tick just isn't offered.
  const happyKey = JSON.stringify(sheets.map((_, i) => bookingsOf(bookings, parts, i)));
  useEffect(() => {
    if (!bookings.length) return;
    const requests: { bookingIds: string[]; shares: BookingShare[] }[] = JSON.parse(happyKey);
    const timer = setTimeout(() => {
      void Promise.all(
        requests.map((r) =>
          r.bookingIds.length || r.shares.length
            ? billsApi.happyHourDiscount(r.bookingIds, r.shares).then((x) => x.discount, () => 0)
            : Promise.resolve(0),
        ),
      ).then(setHappyHours);
    }, 300);
    return () => clearTimeout(timer);
  }, [happyKey]);

  // Kept as it changes, so closing the modal in any way (Cancel, ✕, Escape, a tap outside) loses nothing; dropped once
  // the bills are generated.
  useEffect(() => {
    if (done) clearBillDraft(openedWith);
    else saveBillDraft(openedWith, { step, active, sheets, parts });
  }, [openedWith, done, step, active, sheets, parts]);

  const salesById = useMemo(() => new Map((sales ?? []).map((s) => [s.id, s])), [sales]);
  const sheetSales = useMemo(
    () => sheets.map((sh) => sh.saleIds.flatMap((id) => (salesById.has(id) ? [salesById.get(id)!] : []))),
    [sheets, salesById],
  );
  const selectedSales = sheetSales[active] ?? [];
  /** Tabs on any of the bills (a tab goes on one bill only). */
  const takenSaleIds = useMemo(() => new Set(sheetSales.flat().map((s) => s.id)), [sheetSales]);

  // A draft left on step 2 whose tabs have since been billed or deleted has nothing on it: back to step 1.
  useEffect(() => {
    if (step === 'payment' && sales && !bookings.length && !takenSaleIds.size) setStep('items');
  }, [step, sales, bookings.length, takenSaleIds.size]);

  const allBookings = useMemo(() => sheets.map((_, i) => sheetBookings(bookings, parts, i)), [sheets.length, bookings, parts]);

  // Memoised on its inputs: the totals stay the same objects while nothing that affects money changes.
  const allTotals = useMemo<BillTotals[]>(
    () =>
      sheets.map((sh, i) =>
        computeBill({
          bookings: allBookings[i]!,
          condimentsTotal: sheetSales[i]!.reduce((a, s) => a + s.total, 0),
          saleCount: sheetSales[i]!.length,
          discount: sh.discount,
          status: sh.status,
          method: sh.method,
          upi: sh.upi,
          cash: sh.cash,
          paid: sh.paid,
        }),
      ),
    [sheets, allBookings, sheetSales],
  );
  const totals = allTotals[active] ?? allTotals[0]!;

  /**
   * After Split, every bill must be shown on step 2 before they can be generated, so a second bill is never generated
   * unseen. `seen[i]` is what bill i held when it was last shown (its table time and tabs); if that changes later
   * (Back to step 1, a bill taken out), it counts as not seen again.
   */
  const reviewKeys = useMemo(
    () => allTotals.map((t, i) => `${t.bookingsTotal}|${t.condimentsTotal}|${sheets[i]?.saleIds.join(',') ?? ''}`),
    [allTotals, sheets],
  );
  const [seen, setSeen] = useState<(string | null)[]>([]);
  const activeKey = reviewKeys[active];
  useEffect(() => {
    if (step !== 'payment' || activeKey === undefined) return;
    setSeen((list) => {
      if (list[active] === activeKey) return list;
      const next = [...list];
      next[active] = activeKey;
      return next;
    });
  }, [step, active, activeKey]);
  const reviewed = useMemo(() => reviewKeys.map((k, i) => seen[i] === k), [reviewKeys, seen]);

  /** Why the bills can't be generated yet: the first bill with something missing. */
  const blocker = useMemo(() => {
    const i = allTotals.findIndex((t) => t.blocker);
    if (i < 0) return null;
    return isSplit ? `${sheetLabel(sheets[i]!, i)}: ${allTotals[i]!.blocker}` : allTotals[i]!.blocker;
  }, [allTotals, isSplit, sheets]);

  // Stable callbacks (with the useState setters, and the shown bill read through a ref), so memoised children don't
  // re-render for new function objects.
  const activeRef = useRef(active);
  activeRef.current = active;
  const updateSheet = useCallback(
    (patch: Partial<BillSheet>) => setSheets((list) => list.map((sh, i) => (i === activeRef.current ? { ...sh, ...patch } : sh))),
    [],
  );
  // A split bill is named after its customer once one is picked (the chip and its booking lines show it).
  const setCustomer = useCallback(
    (customer: CustomerDTO | null) => updateSheet(customer ? { customer, name: customer.name } : { customer }),
    [updateSheet],
  );
  /**
   * Step 1 after Split: the shown bill's customer, picked from the existing customers (loaded for step 2's card), or
   * a typed name, which leaves the bill with no customer.
   */
  const setSheetCustomer = useCallback(
    (name: string, customerId: string | null) => {
      const index = activeRef.current;
      updateSheet({ name, customer: null });
      if (!customerId) return;
      customersApi.get(customerId).then(
        (c) => setSheets((list) => list.map((sh, i) => (i === index && sh.name === name ? { ...sh, customer: c, name: c.name } : sh))),
        () => undefined,
      );
    },
    [updateSheet],
  );
  const setDiscount = useCallback((discount: string) => updateSheet({ discount }), [updateSheet]);
  const setStatus = useCallback((status: BillStatus) => updateSheet({ status }), [updateSheet]);
  const setMethod = useCallback((method: PaymentMethod) => updateSheet({ method }), [updateSheet]);
  const setUpi = useCallback((upi: string) => updateSheet({ upi }), [updateSheet]);
  const setCash = useCallback((cash: string) => updateSheet({ cash }), [updateSheet]);
  const setPaid = useCallback((paid: string) => updateSheet({ paid }), [updateSheet]);

  const toggleSale = useCallback(
    (saleId: string) =>
      setSheets((list) =>
        list.map((sh, i) => {
          if (i !== activeRef.current) return sh;
          const has = sh.saleIds.includes(saleId);
          return { ...sh, saleIds: has ? sh.saleIds.filter((id) => id !== saleId) : [...sh.saleIds, saleId] };
        }),
      ),
    [],
  );

  /** Saves missed items on a tab straight away (the tab is the record; the bill copies it). Throws on failure. */
  const saveTab = useCallback(async (saleId: string, items: CondimentQty[]) => {
    const updated = await condimentSalesApi.update(saleId, { items });
    setSales((list) => (list ?? []).map((s) => (s.id === saleId ? updated : s)));
  }, []);

  /** Opens a new tab from inside the bill and puts it on the bill shown. Throws on failure. */
  const createTab = useCallback(
    async (tabName: string, items: CondimentQty[]) => {
      const created = await condimentSalesApi.create({ tabName, items });
      setSales((list) => [created, ...(list ?? [])]);
      setSheets((list) => list.map((sh, i) => (i === activeRef.current ? { ...sh, saleIds: [...sh.saleIds, created.id] } : sh)));
      void refreshOpenSales();
    },
    [refreshOpenSales],
  );

  /** One bill → two, every booking shared equally; the second bill is shown. */
  const split = useCallback(() => {
    setSheets((list) => (list.length > 1 ? list : [...list, newSheet()]));
    setParts(equalParts(bookings, 2));
    setActive(1);
  }, [bookings]);

  /** Another bill, with nothing on it yet; it is shown. */
  const addSheet = useCallback(() => {
    if (sheets.length < 2 || sheets.length >= MAX_BILLS) return;
    setSheets((list) => [...list, newSheet()]);
    setParts((p) => (p ? addBill(p) : p));
    setActive(sheets.length);
  }, [sheets.length]);

  /** A bill taken out: its parts and tabs go back to the first bill (the second, when the first goes). */
  const removeSheet = useCallback(
    (index: number) => {
      if (sheets.length < 2) return;
      const to = index === 0 ? 1 : 0;
      setSheets((list) =>
        list
          .map((sh, i) => (i === to ? { ...sh, saleIds: [...sh.saleIds, ...list[index]!.saleIds] } : sh))
          .filter((_, i) => i !== index),
      );
      setParts((p) => (p && sheets.length > 2 ? removeBill(p, index) : null));
      setSeen((list) => list.filter((_, i) => i !== index));
      // The bill that took its parts is first afterwards either way.
      setActive((a) => (a === index ? 0 : a > index ? a - 1 : a));
      setEditingTab(null);
    },
    [sheets.length],
  );

  /** The bill shown takes `value` of one booking; the rest goes to the other bills. */
  const setBookingPart = useCallback(
    (bookingId: string, value: number) =>
      setParts((p) => {
        const b = bookings.find((x) => x.id === bookingId);
        if (!p || !b || !p[bookingId]) return p;
        return { ...p, [bookingId]: setPart(p[bookingId]!, b.amount ?? 0, activeRef.current, value) };
      }),
    [bookings],
  );

  /** The bill shown pays `value` of the table time, shared over the bookings by their amounts. */
  const setSheetTotal = useCallback(
    (value: number) => setParts((p) => (p ? setBillTotal(bookings, p, activeRef.current, value) : p)),
    [bookings],
  );

  /** Every booking shared equally between the bills again. */
  const resetEqual = useCallback(() => setParts(equalParts(bookings, sheets.length)), [bookings, sheets.length]);

  const submit = async () => {
    if (blocker || submitting || done || (isSplit && reviewed.includes(false))) return;
    setSubmitting(true);
    setError(null);
    try {
      const requests: CreateBillRequest[] = sheets.map((sh, i) => {
        const t = allTotals[i]!;
        const { bookingIds, shares } = bookingsOf(bookings, parts, i);
        return {
          bookingIds,
          shares: isSplit ? shares : undefined,
          name: isSplit && sh.name.trim() ? sh.name.trim() : undefined,
          condimentSaleIds: sheetSales[i]!.map((s) => s.id),
          customerId: sh.customer?.id ?? null,
          discount: t.discount,
          status: sh.status,
          payment: t.showPayment
            ? { method: sh.method, upi: sh.method === 'split' ? toAmount(sh.upi) : 0, cash: sh.method === 'split' ? toAmount(sh.cash) : 0 }
            : null,
          paidAmount: sh.status === 'partial' ? toAmount(sh.paid) : undefined,
        };
      });
      if (isSplit) await createBills({ bills: requests });
      else await createBill(requests[0]!);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : isSplit ? 'Could not generate the bills' : 'Could not generate the bill');
    } finally {
      setSubmitting(false);
    }
  };

  // Success: the button shows a tick for a moment, then the modal closes. Closing it earlier clears the timer.
  // (onDone is read through a ref: callers pass it inline, and a re-render must not restart the timer.)
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => onDoneRef.current(), SUCCESS_MS);
    return () => clearTimeout(timer);
  }, [done]);

  return {
    step,
    setStep,
    billNumber,
    bookings,
    sales,
    salesError,
    // The bills (one until Split) and the one shown.
    sheets,
    active,
    setActive,
    isSplit,
    split,
    addSheet,
    removeSheet,
    allTotals,
    grandTotal: allTotals.reduce((a, t) => a + t.total, 0),
    // The split: each booking's amount on each bill.
    parts,
    setBookingPart,
    setSheetTotal,
    resetEqual,
    // The bill shown.
    sheetBookings: allBookings[active] ?? [],
    selectedSales,
    takenSaleIds,
    toggleSale,
    editingTab,
    setEditingTab,
    saveTab,
    createTab,
    totals,
    blocker,
    /** After Split: whether each bill has been shown on step 2 as it is now (all must be, to generate). */
    reviewed,
    customer: sheet.customer,
    setCustomer,
    setSheetCustomer,
    sheetName: sheet.name,
    discount: sheet.discount,
    setDiscount,
    happyHour: happyHours[active] ?? 0,
    status: sheet.status,
    setStatus,
    method: sheet.method,
    setMethod,
    upi: sheet.upi,
    setUpi,
    cash: sheet.cash,
    setCash,
    paid: sheet.paid,
    setPaid,
    submit,
    submitting,
    done,
    error,
  };
}

/** "Bill 2", or the bill's own name once given. */
export function sheetLabel(sheet: BillSheet, index: number): string {
  return sheet.name.trim() || sheet.customer?.name || `Bill ${index + 1}`;
}

export type BillForm = ReturnType<typeof useBillForm>;
